import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { useCustomPackages } from '@/api/domains/custom-packages';
import { useUpdateMerchantSettings } from '@/api/domains/merchant-settings';
import { predefinedTemplatesFor, usePackageTemplates } from '@/api/domains/package-templates';
import type { SendingMethod } from '@/api/types/common';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toApiProblem, toastProblem } from '@/lib/errors';
import { LABEL_FORMAT_OPTIONS } from '@/lib/label-formats';

import { SettingsCard, SettingsRow, SettingsSection } from '../components/SettingsSection';

const DEFAULT_LABEL_FORMAT = 'application/pdf;format=A6';

const SENDING_METHODS: SendingMethod[] = ['courier', 'parcel_locker', 'pudo', 'hub'];
const DEFAULT_SENDING_METHOD: SendingMethod = 'courier';

interface ShippingDefaultsSectionProps {
  labelFormat: string | null;
  insuranceValue: number | null;
  sendingMethod: SendingMethod | null;
  /** Default package size: predefined template slug or `custom:{id}`; null = predefined default. */
  defaultPackageKey: string | null;
  eoriNumber: string | null;
  /** Indicative home-market insurance currency label; the actually-applied currency is destination-resolved per order. */
  insuranceCurrency: string | null;
}

/**
 * Per-shipment fallbacks: default label format, default insurance value
 * (applied per shipment under the destination-resolved insurance currency)
 * and the EORI number for customs. Saves instantly - the select on change,
 * the inputs on blur - through disjoint mutations.
 */
export function ShippingDefaultsSection({
  labelFormat,
  insuranceValue,
  sendingMethod,
  defaultPackageKey,
  eoriNumber,
  insuranceCurrency,
}: ShippingDefaultsSectionProps) {
  const { t } = useTranslation('settings');
  const tShipments = useTranslation('shipments').t;

  const templates = usePackageTemplates();
  const customPackages = useCustomPackages();

  const updateFormat = useUpdateMerchantSettings();
  const updateInsurance = useUpdateMerchantSettings();
  const updateSending = useUpdateMerchantSettings();
  const updatePackage = useUpdateMerchantSettings();
  const updateEori = useUpdateMerchantSettings();

  // The dropdown only offers printable formats; fall back to the default if a
  // merchant has a legacy value (e.g. a removed `+json` variant) that no option
  // matches, so the select never renders blank.
  const storedFormat = labelFormat ?? DEFAULT_LABEL_FORMAT;
  const currentFormat = LABEL_FORMAT_OPTIONS.some((option) => option.value === storedFormat)
    ? storedFormat
    : DEFAULT_LABEL_FORMAT;

  const [insuranceInput, setInsuranceInput] = useState(
    insuranceValue == null ? '' : String(insuranceValue),
  );
  const [eoriInput, setEoriInput] = useState(eoriNumber ?? '');

  // Re-sync the inputs when saved settings arrive/refresh.
  useEffect(() => {
    setInsuranceInput(insuranceValue == null ? '' : String(insuranceValue));
  }, [insuranceValue]);

  useEffect(() => {
    setEoriInput(eoriNumber ?? '');
  }, [eoriNumber]);

  const saveFormat = (value: string) => {
    if (value === currentFormat || updateFormat.isPending) return;
    updateFormat.mutate(
      { settings: { default_label_format: value } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  const saveInsurance = () => {
    const raw = insuranceInput.trim();
    const next = raw === '' ? null : Number(raw);
    if (next !== null && (!Number.isFinite(next) || next < 0)) {
      setInsuranceInput(insuranceValue == null ? '' : String(insuranceValue));
      return;
    }
    if (next === insuranceValue) return;
    updateInsurance.mutate(
      { settings: { default_insurance_value: next } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setInsuranceInput(insuranceValue == null ? '' : String(insuranceValue));
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  const currentSendingMethod = sendingMethod ?? DEFAULT_SENDING_METHOD;

  const saveSendingMethod = (value: string) => {
    const next = value as SendingMethod;
    if (next === currentSendingMethod || updateSending.isPending) return;
    updateSending.mutate(
      { settings: { default_sending_method: next } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  // Predefined sizes are per destination market; the account-level default
  // is a slug, so offer the union across markets (dimensions labelled from the
  // first market defining the slug). Mirror the label-format legacy fallback:
  // a stored key that no option matches (deleted custom package) renders as
  // the predefined default rather than a blank select.
  const predefined = predefinedTemplatesFor(templates.data);
  const predefinedDefaultKey =
    predefined.find((tpl) => tpl.is_default)?.key ??
    predefined[0]?.key ??
    customPackages.data?.[0]?.key ??
    '';
  const storedPackageKey = defaultPackageKey ?? predefinedDefaultKey;
  const currentPackageKey =
    predefined.some((tpl) => tpl.key === storedPackageKey) ||
    customPackages.data?.some((pkg) => pkg.key === storedPackageKey)
      ? storedPackageKey
      : predefinedDefaultKey;

  const savePackageKey = (value: string) => {
    if (value === currentPackageKey || updatePackage.isPending) return;
    updatePackage.mutate(
      { settings: { default_package_key: value } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => toastProblem(toApiProblem(error)),
      },
    );
  };

  const saveEori = () => {
    const next = eoriInput.trim() === '' ? null : eoriInput.trim();
    if (next === (eoriNumber ?? null)) return;
    updateEori.mutate(
      { settings: { eori_number: next } },
      {
        onSuccess: () => toast.success(t('saved')),
        onError: (error) => {
          setEoriInput(eoriNumber ?? '');
          toastProblem(toApiProblem(error));
        },
      },
    );
  };

  return (
    <SettingsSection
      title={t('general.shippingDefaults.title')}
      description={t('general.shippingDefaults.description')}
    >
      <SettingsCard>
        <SettingsRow
          label={t('general.shippingDefaults.labelFormat')}
          description={t('general.shippingDefaults.labelFormatHint')}
        >
          <Select value={currentFormat} onValueChange={saveFormat}>
            <SelectTrigger className="w-40" aria-label={t('general.shippingDefaults.labelFormat')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LABEL_FORMAT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingsRow>
        <SettingsRow
          label={t('general.shippingDefaults.sendingMethod')}
          description={t('general.shippingDefaults.sendingMethodHint')}
        >
          <Select value={currentSendingMethod} onValueChange={saveSendingMethod}>
            <SelectTrigger
              className="w-40"
              aria-label={t('general.shippingDefaults.sendingMethod')}
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SENDING_METHODS.map((method) => (
                <SelectItem key={method} value={method}>
                  {tShipments(`create.sendingMethods.${method}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </SettingsRow>
        <SettingsRow
          label={t('general.shippingDefaults.packageSize')}
          description={t('general.shippingDefaults.packageSizeHint')}
        >
          <Select
            value={currentPackageKey}
            onValueChange={savePackageKey}
            disabled={templates.isLoading || customPackages.isLoading}
          >
            <SelectTrigger className="w-56" aria-label={t('general.shippingDefaults.packageSize')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {predefined.length ? (
                <SelectGroup>
                  {predefined.map((tpl) => (
                    <SelectItem key={tpl.key} value={tpl.key}>
                      {`${tpl.name} - ${tpl.height} x ${tpl.width} x ${tpl.length} ${tpl.dimension_unit.toLowerCase()}`}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ) : null}
              {customPackages.data?.length ? (
                <>
                  {predefined.length ? <SelectSeparator /> : null}
                  <SelectGroup>
                    <SelectLabel>{tShipments('packageGroups.custom')}</SelectLabel>
                    {customPackages.data.map((pkg) => (
                      <SelectItem key={pkg.key} value={pkg.key}>
                        {`${pkg.name} - ${pkg.height} x ${pkg.width} x ${pkg.length} ${pkg.dimension_unit.toLowerCase()}`}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                </>
              ) : null}
            </SelectContent>
          </Select>
        </SettingsRow>
        <SettingsRow label={t('general.shippingDefaults.insurance')}>
          <div className="flex items-center gap-2">
            <Input
              type="number"
              inputMode="decimal"
              min={0}
              step={0.01}
              placeholder="0.00"
              value={insuranceInput}
              onChange={(event) => setInsuranceInput(event.target.value)}
              onBlur={saveInsurance}
              aria-label={t('general.shippingDefaults.insurance')}
              className="h-9 w-28 text-right"
            />
            {insuranceCurrency ? (
              <span className="text-[13px] text-muted-foreground">{insuranceCurrency}</span>
            ) : null}
          </div>
        </SettingsRow>
        <SettingsRow
          label={t('general.shippingDefaults.eori')}
          description={t('general.shippingDefaults.eoriHint')}
        >
          <Input
            value={eoriInput}
            placeholder={t('general.shippingDefaults.eoriPlaceholder')}
            onChange={(event) => setEoriInput(event.target.value)}
            onBlur={saveEori}
            aria-label={t('general.shippingDefaults.eori')}
            className="h-9 w-48"
          />
        </SettingsRow>
      </SettingsCard>
    </SettingsSection>
  );
}
